import React, { useEffect, useState, useCallback } from 'react';

const gold = '#D4AF37';
const ivory = '#F7E6BD';
const ivorySoft = 'rgba(247,230,189,0.9)';
const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

const SECTIONS = [
  { id: 'transmission', label: "L'Art de la Transmission", hint: 'FAQ + Aide — visible par tous' },
  { id: 'charte', label: 'La Charte du Gardien', hint: 'Engagements + Sécurité' },
  { id: 'guide', label: "Mode d'Emploi", hint: 'Guide structuré par thèmes (utilisez le champ Thème pour grouper)' },
];

const inputStyle = {
  width: '100%',
  fontFamily: "'Georgia', serif",
  fontSize: 14,
  color: ivory,
  background: 'rgba(0,0,0,0.4)',
  border: '1px solid rgba(212,175,55,0.25)',
  borderRadius: 8,
  padding: '10px 12px',
  outline: 'none',
  resize: 'vertical',
};

const goldBtn = (kind = 'primary') => ({
  fontFamily: "'Georgia', serif",
  fontSize: 12,
  letterSpacing: 1,
  textTransform: 'uppercase',
  padding: '8px 16px',
  borderRadius: 16,
  cursor: 'pointer',
  fontWeight: 600,
  border: '1px solid #8B6914',
  background: kind === 'primary'
    ? 'linear-gradient(145deg, #D4AF37, #B8860B)'
    : 'transparent',
  color: kind === 'primary' ? '#1a1100' : gold,
  transition: 'all 0.2s',
});

const dangerBtn = {
  fontFamily: "'Georgia', serif",
  fontSize: 11,
  letterSpacing: 0.5,
  padding: '6px 12px',
  borderRadius: 14,
  cursor: 'pointer',
  border: '1px solid rgba(192,57,43,0.4)',
  background: 'transparent',
  color: '#E07B5B',
  transition: 'all 0.2s',
};

const UniversAdminTab = ({ adminKey }) => {
  const [activeSection, setActiveSection] = useState('transmission');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ q: '', a: '', theme: '' });
  const [creating, setCreating] = useState(false);

  const headers = { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' };

  const fetchItems = useCallback(async (section) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/univers/${section}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems(activeSection);
    setEditingId(null);
    setCreating(false);
  }, [activeSection, fetchItems]);

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft({ q: item.q, a: item.a, theme: item.theme || '' });
    setCreating(false);
  };

  const startCreate = () => {
    setCreating(true);
    setEditingId(null);
    setDraft({ q: '', a: '', theme: '' });
  };

  const cancel = () => {
    setEditingId(null);
    setCreating(false);
    setDraft({ q: '', a: '', theme: '' });
  };

  const saveItem = async () => {
    if (!draft.q.trim() || !draft.a.trim()) {
      alert('La question et la réponse ne peuvent pas être vides.');
      return;
    }
    const body = { q: draft.q.trim(), a: draft.a.trim() };
    // Le thème est supporté par les 3 sections (transmission, charte, guide).
    // Envoyer une chaîne vide permet au backend d'effacer le thème si l'admin le vide.
    body.theme = draft.theme.trim();
    try {
      if (creating) {
        const res = await fetch(`${API}/admin/univers/${activeSection}`, {
          method: 'POST', headers, body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else if (editingId) {
        const res = await fetch(`${API}/admin/univers/${activeSection}/${editingId}`, {
          method: 'PUT', headers, body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
      cancel();
      await fetchItems(activeSection);
    } catch (e) {
      alert(`Erreur d'enregistrement : ${e.message}`);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Supprimer définitivement « ${item.q} » ?`)) return;
    try {
      const res = await fetch(`${API}/admin/univers/${activeSection}/${item.id}`, {
        method: 'DELETE', headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchItems(activeSection);
    } catch (e) {
      alert(`Erreur de suppression : ${e.message}`);
    }
  };

  const moveItem = async (idx, dir) => {
    const newOrder = [...items];
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= newOrder.length) return;
    [newOrder[idx], newOrder[swapWith]] = [newOrder[swapWith], newOrder[idx]];
    setItems(newOrder); // Optimistic UI
    try {
      const res = await fetch(`${API}/admin/univers/${activeSection}/reorder`, {
        method: 'POST', headers, body: JSON.stringify({ ids: newOrder.map(i => i.id) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      alert(`Erreur de réorganisation : ${e.message}`);
      await fetchItems(activeSection);
    }
  };

  const resetSection = async () => {
    if (!window.confirm('Restaurer le contenu d\'origine de cette section ? TOUTES vos modifications seront perdues.')) return;
    try {
      const res = await fetch(`${API}/admin/univers/${activeSection}/reset`, {
        method: 'POST', headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchItems(activeSection);
    } catch (e) {
      alert(`Erreur de réinitialisation : ${e.message}`);
    }
  };

  return (
    <div data-testid="admin-univers-tab">
      {/* Sélecteur de section */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {SECTIONS.map(s => (
          <button
            key={s.id}
            data-testid={`univers-admin-section-${s.id}`}
            onClick={() => setActiveSection(s.id)}
            style={{
              fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 18px',
              borderRadius: 8, cursor: 'pointer', transition: 'all 0.3s',
              border: activeSection === s.id ? `1px solid ${gold}` : '1px solid rgba(212,175,55,0.2)',
              background: activeSection === s.id ? 'rgba(212,175,55,0.08)' : 'transparent',
              color: activeSection === s.id ? gold : ivorySoft,
              fontWeight: activeSection === s.id ? 600 : 400,
            }}
          >
            {s.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button data-testid="univers-admin-add-btn" onClick={startCreate} style={goldBtn('primary')}>
          + Nouvelle entrée
        </button>
        <button data-testid="univers-admin-reset-btn" onClick={resetSection} style={goldBtn('ghost')} title="Restaurer le contenu d'origine">
          Restaurer
        </button>
      </div>

      {/* Description de la section */}
      <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: ivorySoft, fontStyle: 'italic', marginBottom: 20 }}>
        {SECTIONS.find(s => s.id === activeSection)?.hint}
      </p>

      {/* Bloc "Question du mois" — visible uniquement pour la section guide */}
      {activeSection === 'guide' && (
        <QuestionDuMoisEditor adminKey={adminKey} />
      )}

      {/* Formulaire création */}
      {creating && (
        <div data-testid="univers-admin-create-form" style={{
          border: '1px solid rgba(212,175,55,0.3)', borderRadius: 10, padding: 18, marginBottom: 20,
          background: 'rgba(212,175,55,0.03)',
        }}>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: gold, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 12, fontWeight: 600 }}>
            Nouvelle entrée
          </p>
          {/* Le champ Thème est disponible pour TOUTES les sections (transmission/charte/guide) */}
          <input
            data-testid="univers-admin-create-theme"
            placeholder={activeSection === 'transmission' ? 'Thème (ex : ✦ Le Manifeste)' : activeSection === 'charte' ? 'Thème (ex : 🛡️ Confiance & Vie Privée)' : 'Thème (ex : 🚀 Le Tamis Soyeux)'}
            value={draft.theme}
            onChange={e => setDraft({ ...draft, theme: e.target.value })}
            style={{ ...inputStyle, marginBottom: 10 }}
          />
          <input
            data-testid="univers-admin-create-q"
            placeholder="Question / Titre"
            value={draft.q}
            onChange={e => setDraft({ ...draft, q: e.target.value })}
            style={{ ...inputStyle, marginBottom: 10 }}
          />
          <textarea
            data-testid="univers-admin-create-a"
            placeholder="Réponse / Contenu"
            rows={5}
            value={draft.a}
            onChange={e => setDraft({ ...draft, a: e.target.value })}
            style={inputStyle}
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
            <button data-testid="univers-admin-create-cancel" onClick={cancel} style={goldBtn('ghost')}>Annuler</button>
            <button data-testid="univers-admin-create-save" onClick={saveItem} style={goldBtn('primary')}>Enregistrer</button>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <p style={{ fontFamily: "'Georgia', serif", color: ivorySoft, textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
          Chargement...
        </p>
      ) : items.length === 0 ? (
        <p style={{ fontFamily: "'Georgia', serif", color: ivorySoft, textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
          Aucune entrée. Cliquez sur « + Nouvelle entrée » pour commencer.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((item, idx) => (
            <div
              key={item.id}
              data-testid={`univers-admin-item-${idx}`}
              style={{
                border: '1px solid rgba(212,175,55,0.18)', borderRadius: 10, padding: 16,
                background: 'rgba(0,0,0,0.3)',
              }}
            >
              {editingId === item.id ? (
                <>
                  <input
                    data-testid={`univers-admin-edit-theme-${idx}`}
                    placeholder="Thème"
                    value={draft.theme}
                    onChange={e => setDraft({ ...draft, theme: e.target.value })}
                    style={{ ...inputStyle, marginBottom: 10 }}
                  />
                  <input
                    data-testid={`univers-admin-edit-q-${idx}`}
                    value={draft.q}
                    onChange={e => setDraft({ ...draft, q: e.target.value })}
                    style={{ ...inputStyle, marginBottom: 10 }}
                  />
                  <textarea
                    data-testid={`univers-admin-edit-a-${idx}`}
                    rows={4}
                    value={draft.a}
                    onChange={e => setDraft({ ...draft, a: e.target.value })}
                    style={inputStyle}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                    <button data-testid={`univers-admin-edit-cancel-${idx}`} onClick={cancel} style={goldBtn('ghost')}>Annuler</button>
                    <button data-testid={`univers-admin-edit-save-${idx}`} onClick={saveItem} style={goldBtn('primary')}>Enregistrer</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ color: gold, fontSize: 11, opacity: 0.4 }}>#{idx + 1}</span>
                    {item.theme && (
                      <span style={{
                        fontFamily: "'Georgia', serif", fontSize: 11, color: gold,
                        fontStyle: 'italic', padding: '2px 8px', borderRadius: 10,
                        background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)',
                      }}>{item.theme}</span>
                    )}
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: gold, fontWeight: 600, margin: 0, flex: 1 }}>
                      {item.q}
                    </p>
                  </div>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: ivorySoft, lineHeight: 1.7, margin: '0 0 12px', whiteSpace: 'pre-line' }}>
                    {item.a}
                  </p>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', borderTop: '1px solid rgba(212,175,55,0.1)', paddingTop: 10 }}>
                    <button
                      data-testid={`univers-admin-up-${idx}`}
                      onClick={() => moveItem(idx, -1)}
                      disabled={idx === 0}
                      title="Monter"
                      style={{ ...goldBtn('ghost'), opacity: idx === 0 ? 0.3 : 1, padding: '4px 10px', fontSize: 11 }}
                    >↑</button>
                    <button
                      data-testid={`univers-admin-down-${idx}`}
                      onClick={() => moveItem(idx, 1)}
                      disabled={idx === items.length - 1}
                      title="Descendre"
                      style={{ ...goldBtn('ghost'), opacity: idx === items.length - 1 ? 0.3 : 1, padding: '4px 10px', fontSize: 11 }}
                    >↓</button>
                    <div style={{ flex: 1 }} />
                    <button data-testid={`univers-admin-edit-${idx}`} onClick={() => startEdit(item)} style={goldBtn('ghost')}>
                      Modifier
                    </button>
                    <button data-testid={`univers-admin-delete-${idx}`} onClick={() => deleteItem(item)} style={dangerBtn}>
                      Supprimer
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UniversAdminTab;

/* ── Question du mois — éditeur singleton ── */
function QuestionDuMoisEditor({ adminKey }) {
  const [data, setData] = useState({ q: '', a: '', active: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const headers = { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/univers/question-du-mois`, { headers });
      const d = await r.json();
      setData({
        q: d.item?.q || '',
        a: d.item?.a || '',
        active: !!d.item?.active,
      });
    } catch { /* silent */ }
    setLoading(false);
  }, [adminKey]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!data.q.trim() || !data.a.trim()) {
      alert('La question et la réponse sont requises.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/univers/question-du-mois`, {
        method: 'PUT', headers,
        body: JSON.stringify({ q: data.q.trim(), a: data.a.trim(), active: data.active }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEditing(false);
      await load();
    } catch (e) {
      alert(`Erreur d'enregistrement : ${e.message}`);
    }
    setSaving(false);
  };

  const toggleActive = async () => {
    if (!data.q.trim()) {
      alert('Saisissez une question avant d\'activer.');
      return;
    }
    setSaving(true);
    try {
      await fetch(`${API}/admin/univers/question-du-mois`, {
        method: 'PUT', headers,
        body: JSON.stringify({ q: data.q.trim(), a: data.a.trim(), active: !data.active }),
      });
      await load();
    } catch { /* silent */ }
    setSaving(false);
  };

  if (loading) return null;

  return (
    <div data-testid="qdm-editor" style={{
      border: '1px solid rgba(212,175,55,0.3)', borderRadius: 10, padding: 18, marginBottom: 22,
      background: 'rgba(212,175,55,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37',
          letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600,
        }}>✦ Question du mois</span>
        <span style={{
          fontFamily: "'Georgia', serif", fontSize: 10, padding: '2px 10px', borderRadius: 12,
          background: data.active ? 'rgba(123,201,123,0.15)' : 'rgba(247,230,189,0.08)',
          color: data.active ? '#7BC97B' : 'rgba(247,230,189,0.5)',
          border: `1px solid ${data.active ? 'rgba(123,201,123,0.3)' : 'rgba(247,230,189,0.15)'}`,
          letterSpacing: 0.5,
        }}>{data.active ? 'PUBLIÉE' : 'MASQUÉE'}</span>
        <div style={{ flex: 1 }} />
        {!editing && (
          <button data-testid="qdm-toggle" onClick={toggleActive} disabled={saving} style={{
            ...goldBtn('ghost'), fontSize: 11, padding: '6px 12px',
          }}>{data.active ? 'Masquer' : 'Publier'}</button>
        )}
      </div>

      {!editing ? (
        <>
          {data.q ? (
            <>
              <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', fontWeight: 600, margin: '0 0 8px' }}>
                {data.q}
              </p>
              <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(247,230,189,0.85)', lineHeight: 1.7, margin: '0 0 12px', whiteSpace: 'pre-line' }}>
                {data.a}
              </p>
            </>
          ) : (
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(247,230,189,0.6)', fontStyle: 'italic', margin: '0 0 12px' }}>
              Aucune question du mois définie. Cliquez sur « Modifier » pour en saisir une.
            </p>
          )}
          <button data-testid="qdm-edit" onClick={() => setEditing(true)} style={goldBtn('ghost')}>
            Modifier
          </button>
        </>
      ) : (
        <>
          <input
            data-testid="qdm-q"
            placeholder="Question (ex : Bobine est-il accessible aux non-techniques ?)"
            value={data.q}
            onChange={e => setData({ ...data, q: e.target.value })}
            style={{ ...inputStyle, marginBottom: 10 }}
          />
          <textarea
            data-testid="qdm-a"
            placeholder="Réponse"
            rows={4}
            value={data.a}
            onChange={e => setData({ ...data, a: e.target.value })}
            style={inputStyle}
          />
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
            fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(247,230,189,0.85)', cursor: 'pointer',
          }}>
            <input
              data-testid="qdm-active"
              type="checkbox"
              checked={data.active}
              onChange={e => setData({ ...data, active: e.target.checked })}
              style={{ accentColor: '#D4AF37' }}
            />
            Publier sur la page publique
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
            <button data-testid="qdm-cancel" onClick={() => { setEditing(false); load(); }} style={goldBtn('ghost')}>Annuler</button>
            <button data-testid="qdm-save" onClick={save} disabled={saving} style={goldBtn('primary')}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
